import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 60000, // 60s — llamadas AFIP SOAP pueden ser lentas
});

// Attach token from localStorage as fallback (cross-site cookies can be blocked)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("erp_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;

export const fmtARS = (n) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(n || 0);

export const fmtUSD = (n) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(n || 0);

export const fmtMoney = (n, moneda = "ARS") =>
  moneda === "USD" ? fmtUSD(n) : fmtARS(n);

export const fmtNum = (n, digits = 2) =>
  new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n || 0);

export const fmtDate = (iso) => {
  if (!iso) return "-";
  // Parseo local seguro para 'YYYY-MM-DD' (evita shift a UTC → -1 día en AR)
  if (typeof iso === "string") {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = new Date(iso);
  return d.toLocaleDateString("es-AR");
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

// Calcula la fecha de vencimiento aplicando la condición de pago del cliente/proveedor.
// - condicion = null / {tipo: 'contado'}                 → vencimiento = fecha
// - condicion = {tipo: 'dias', dias: N}                  → vencimiento = fecha + N días
// - condicion = {tipo: 'dia_semana', dia_semana, semanas_offset}
//     dia_semana: 0=Lun .. 6=Dom (mismo criterio que Python date.weekday())
//     semanas_offset: 0=esta semana, 1=próxima, 2=subsiguiente
// Alícuotas oficiales de IVA AFIP: hace snap a la más cercana para evitar
// errores de aprobación cuando el ratio iva/subtotal deja decimales sucios.
const IVA_ALICUOTAS_AFIP = [0, 2.5, 5, 10.5, 21, 27];
export const snapIvaAfip = (pct, tolerancia = 0.6) => {
  if (pct == null || pct === "") return pct;
  const v = Number(pct);
  if (!Number.isFinite(v)) return pct;
  for (const oficial of IVA_ALICUOTAS_AFIP) {
    if (Math.abs(v - oficial) <= tolerancia) return oficial;
  }
  return Math.round(v * 100) / 100;
};

export const calcVencimiento = (fechaIso, condicion) => {
  if (!fechaIso) return fechaIso;
  const m = String(fechaIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return fechaIso;
  const [_, y, mo, d] = m;
  const base = new Date(Number(y), Number(mo) - 1, Number(d));
  const tipo = (condicion && condicion.tipo) || "contado";
  if (tipo === "sin_plazo") return null; // sin fecha de vencimiento
  if (tipo === "contado") return fechaIso.slice(0, 10);
  if (tipo === "dias") {
    const dias = Math.max(0, parseInt(condicion.dias || 0, 10));
    base.setDate(base.getDate() + dias);
  } else if (tipo === "dia_semana") {
    // Convert JS getDay (0=Sun..6=Sat) to Python weekday (0=Mon..6=Sun)
    const jsDay = base.getDay();
    const pyDay = (jsDay + 6) % 7;
    const target = Math.max(0, Math.min(6, parseInt(condicion.dia_semana ?? 0, 10)));
    const offset = Math.max(0, parseInt(condicion.semanas_offset ?? 0, 10));
    const delta = ((target - pyDay) % 7 + 7) % 7;
    base.setDate(base.getDate() + delta + offset * 7);
  }
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, "0");
  const dd = String(base.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

